import { useMemo } from "react"
import type { ThemeConfigType } from "@/stores/DeveloperInsightsStore"

interface ThemeClasses {
  card: string
  spacing: string
  borderRadius: string
  typography: string
  colorScheme: string
}

export function getDefaultTheme(): ThemeConfigType {
  return {
    cardVariant: "default",
    colorScheme: "default",
    chartColors: {
      primary: "var(--chart-1)",
      secondary: "var(--chart-2)",
      tertiary: "var(--chart-3)",
      quaternary: "var(--chart-4)",
      quinary: "var(--chart-5)",
    },
    spacing: "normal",
    borderRadius: "medium",
    typographyScale: "normal",
  }
}

export function useTheme(theme: ThemeConfigType): ThemeClasses {
  return useMemo(() => {
    // Card variant classes
    const cardClasses =
      {
        default: "",
        outlined: "border-2",
        elevated: "shadow-lg",
      }[theme.cardVariant] ?? ""

    // Spacing classes
    const spacingClasses =
      {
        compact: "gap-3 py-4",
        normal: "gap-6 py-6",
        comfortable: "gap-8 py-8",
      }[theme.spacing] ?? "gap-6 py-6"

    // Border radius classes
    const borderRadiusClasses =
      {
        none: "rounded-none",
        small: "rounded-md",
        medium: "rounded-xl",
        large: "rounded-2xl",
      }[theme.borderRadius] ?? "rounded-xl"

    // Typography scale classes
    const typographyClasses =
      {
        small: "text-sm",
        normal: "text-base",
        large: "text-lg",
      }[theme.typographyScale] ?? "text-base"

    // Color scheme classes
    const colorSchemeClasses =
      {
        default: "",
        primary: "border-primary/20 bg-primary/5",
        secondary: "border-secondary/20 bg-secondary/5",
        accent: "border-accent/20 bg-accent/5",
      }[theme.colorScheme] ?? ""

    return {
      card: cardClasses,
      spacing: spacingClasses,
      borderRadius: borderRadiusClasses,
      typography: typographyClasses,
      colorScheme: colorSchemeClasses,
    }
  }, [
    theme.cardVariant,
    theme.spacing,
    theme.borderRadius,
    theme.typographyScale,
    theme.colorScheme,
  ])
}

export function getChartColor(theme: ThemeConfigType, index: number = 0): string {
  const colors = [
    theme.chartColors.primary,
    theme.chartColors.secondary,
    theme.chartColors.tertiary,
    theme.chartColors.quaternary,
    theme.chartColors.quinary,
  ]
  return colors[index % colors.length]
}
